import Vue from 'vue';
import Vuex from 'vuex';
import Vuetify from '@/plugins/vuetify';
import { themes } from '@/plugins/themes';
import { GlobalStickerSize, StickerType } from '../utils/constants';

Vue.use(Vuex);

const store = new Vuex.Store({
	state: {
		globalErrorMessage: null,
		panelLoading: false,
		isPanelLoaded: false,
		isExtensionOnline: true,
		isStreamerOnline: true,
		isSubscriber: false,
		theme: 'violet',
		tabView: false, // Display Tabs or Dropdown
		size: 'sm', // For Panel View use 'sm' and for Component View use 'md'
		speed: 9,
		streamerDisplayName: 'Broadcaster',
		viewerDisplayName: null,
		cooldownTime: 0,
		partyModeCooldownTime: 0,
		twitchAuthToken: null,
		userId: null,
		opaqueUserId: null,
		channelId: null,
		role: null,
		extensionVersion: '0.0.1',
		twitchAuth: null,
		isMobile: false, // Twitch detected a mobile user.
		isComponentView: false, // Running as Twitch component vs. panel view.
		isPartyModeOn: false,
		globalStickerSize: GlobalStickerSize.MEDIUM,
		bitsCreditAmount: 0,

		// This is set to true on the initial click of a TTS sticker in twitch-extension/src/components/panel/Stickers/StickerTTS.vue.
		// Then, StickerTab.events.onStickerClick sinks the event, and makes the normal streamer status call.
		// Then, instead of making the show sticker call, it sets isTtsStatusCheckSuccess, which is then used by StickerTTS
		// to continue processing by showing the dialog to prompt for TTS text.
		// This isn't a great solution, but it works for now.
		isDoTtsStatusCheckOnClick: false,
		isTtsStatusCheckSuccess: false,
		clickedTtsSticker: null,

		// Is the streamer configured to manually approve TTS stickers?
		ttsIsManuallyApproveAllStickers: false,

		ttsMessageMaxLength: 256,

		favoriteStickers: [],

		stickerTabItems: [
			{
				id: 0,
				name: 'All',
				active: true,
				isModified: false,
			},
			{
				id: 1,
				name: 'Favorites',
				icon: 'icon_favorite',
				type: StickerType.FAVORITE,
				active: true,
				isModified: false,
			},
			{
				id: 2,
				name: 'TTS',
				icon: 'icon_tts',
				type: StickerType.TTS,
				active: true,
				isModified: false,
			},
			{
				id: 3,
				name: 'Channel',
				icon: 'icon_channel',
				type: StickerType.CUSTOM,
				active: true,
				isModified: false,
			},
			{
				id: 4,
				name: 'Placement',
				icon: 'icon_placement',
				type: StickerType.PLACEMENT,
				active: true,
				isModified: false,
			},
			{
				id: 5,
				name: 'Reward',
				icon: 'icon_reward',
				type: StickerType.REWARD,
				active: true,
				isModified: false,
			},
			{
				id: 6,
				name: 'Sub Only',
				icon: 'icon_subonly',
				type: StickerType.SUB_ONLY,
				active: true,
				isModified: false,
			},
			{
				id: 7,
				name: 'Power',
				icon: 'icon_power',
				type: StickerType.POWER,
				active: false,
				isModified: false,
			},
		],
		stickerCounts: [
			{
				type: StickerType.CUSTOM,
				count: 0,
			},
			{
				type: StickerType.FAVORITE,
				count: 0,
			},
			{
				type: StickerType.POWER,
				count: 0,
			},
			{
				type: StickerType.REWARD,
				count: 0,
			},
			{
				type: StickerType.TTS,
				count: 0,
			},
			{
				type: StickerType.SUB_ONLY,
				count: 0,
			},
			{
				type: StickerType.PLACEMENT,
				count: 0,
			},
		],
		stickersLastModifiedServer: [
			{
				type: StickerType.CUSTOM,
				ts: 0,
			},
			{
				type: StickerType.FAVORITE,
				ts: 0,
			},
			{
				type: StickerType.POWER,
				ts: 0,
			},
			{
				type: StickerType.REWARD,
				ts: 0,
			},
			{
				type: StickerType.TTS,
				ts: 0,
			},
			{
				type: StickerType.SUB_ONLY,
				ts: 0,
			},
			{
				type: StickerType.PLACEMENT,
				ts: 0,
			},
		],
		stickersLastModifiedLocal: [
			{
				type: StickerType.CUSTOM,
				ts: 0,
			},
			{
				type: StickerType.FAVORITE,
				ts: 0,
			},
			{
				type: StickerType.POWER,
				ts: 0,
			},
			{
				type: StickerType.REWARD,
				ts: 0,
			},
			{
				type: StickerType.TTS,
				ts: 0,
			},
			{
				type: StickerType.SUB_ONLY,
				ts: 0,
			},
			{
				type: StickerType.PLACEMENT,
				ts: 0,
			},
		],
		localization: {
			panelTitle: 'Select a sticker to slap it<br>on the live stream!',
		},
		// The response from the panel data call. Used to reload the stickers tab if
		// the viewer clicks another tab, like leaderboard, and then back to the stickers.
		panelServerResponse: null,

		// Defines the current page within a tab, used to support pagination.
		// Currently only used for favorites as they are computed.
		currentPage: 0,
	},
	getters: {
		activeTabItems: state => {
			return state.stickerTabItems.filter(tab => {
				if (tab.type) {
					const count = state.stickerCounts.filter(sc => sc.type == tab.type)[0].count;
					return tab.active && count > 0;
				} else {
					return tab.active;
				}
			});
		},
	},
	mutations: {
		setGlobalErrorMessage(state, value) {
			state.globalErrorMessage = value;
		},
		setPanelLoading: (state, payload) => {
			state.panelLoading = payload;
		},
		setPanelLoaded: (state, payload) => {
			state.isPanelLoaded = payload;
		},
		setExtensionOnline: (state, payload) => {
			state.isExtensionOnline = payload;
		},
		setStreamerOnline: (state, payload) => {
			state.isStreamerOnline = payload;
		},
		setSubscriber: (state, payload) => {
			state.isSubscriber = payload;
		},
		setSpeed: (state, payload) => {
			state.speed = payload;
		},
		setSize: (state, payload) => {
			state.size = payload;
		},
		// Called from StickersTabData.setDataAfterServerCall, when we get the
		// streamer's configured stream.
		setTheme: (state, payload) => {
			localStorage.setItem('theme', payload);
			state.theme = payload;
			Vuetify.framework.theme.themes['light'] = themes[payload];

			// if (payload === 'violet') {
			//   Vuetify.framework.theme.themes['light'] = violet
			// }
			// if (payload === 'blue') {
			//   Vuetify.framework.theme.themes['light'] = blue
			// }
			// if (payload === 'red') {
			//   Vuetify.framework.theme.themes['light'] = red
			// }
			// if (payload === 'orange') {
			//   Vuetify.framework.theme.themes['light'] = orange
			// }
		},
		setTwitchAuthToken: (state, payload) => {
			state.twitchAuthToken = payload;
		},
		setUserId: (state, payload) => {
			state.userId = payload;
		},
		setOpaqueUserId: (state, payload) => {
			state.opaqueUserId = payload;
		},
		setChannelId: (state, payload) => {
			state.channelId = payload;
		},
		setRole: (state, payload) => {
			state.role = payload;
		},
		setStreamerDisplayName: (state, payload) => {
			state.streamerDisplayName = payload;
		},
		setViewerDisplayName: (state, payload) => {
			state.viewerDisplayName = payload;
		},
		setCooldownTime: (state, payload) => {
			state.cooldownTime = payload;
		},
		setPartyModeCooldownTime: (state, payload) => {
			state.partyModeCooldownTime = payload;
		},
		setTwitchAuth: (state, payload) => {
			state.twitchAuth = payload;
		},
		setExtensionVersion: (state, payload) => {
			state.extensionVersion = payload;
		},
		setIsMobile: (state, payload) => {
			state.isMobile = payload;
		},
		setIsComponentView: (state, payload) => {
			state.isComponentView = payload;
		},
		setBitsCreditAmount: (state, payload) => {
			state.bitsCreditAmount = payload;
		},

		setIsDoTtsStatusCheckOnClick: (state, payload) => {
			state.isDoTtsStatusCheckOnClick = payload;
		},
		setIsTtsStatusCheckSuccess: (state, payload) => {
			state.isTtsStatusCheckSuccess = payload;
		},
		setClickedTtsSticker: (state, payload) => {
			state.clickedTtsSticker = { ...payload };
		},

		setTtsIsManuallyApproveAllStickers: (state, payload) => {
			state.ttsIsManuallyApproveAllStickers = payload;
		},

		setTtsMessageMaxLength: (state, payload) => {
			state.ttsMessageMaxLength = payload;
		},

		addAllFavoriteStickers: (state, payload) => {
			state.favoriteStickers = payload;
		},

		addFavoriteSticker: (state, payload) => {
			state.favoriteStickers.push(payload);
		},

		removeFavoriteSticker: (state, payload) => {
			state.favoriteStickers = state.favoriteStickers.filter(
				f => !(f.id == payload.id && f.stickerType == payload.stickerType),
			);
		},

		setIsPartyModeOn: (state, payload) => {
			state.isPartyModeOn = payload;
		},

		setGlobalStickerSize: (state, payload) => {
			state.globalStickerSize = payload;
		},

		setStickerCounts: (state, payload) => {
			state.stickerCounts = payload;
		},
		setServerStickersLastModified: (state, payload) => {
			for (const existing of state.stickersLastModifiedServer) {
				const p = findItemByType(payload, existing.type);
				if (p) {
					existing.ts = p.ts;

					const localTs = findItemByType(state.stickersLastModifiedLocal, existing.type);
					const isModified = existing.ts > localTs.ts;
					for (const tabItem of state.stickerTabItems) {
						if (tabItem.type && tabItem.type == p.type) {
							tabItem.isModified = isModified;
							break;
						}
					}
					break;
				}
			}
		},
		setLocalStickerLastModified: (state, payload) => {
			for (const existing of state.stickersLastModifiedLocal) {
				const p = findItemByType(payload, existing.type);
				if (p) {
					existing.ts = p.ts;

					const serverTs = findItemByType(state.stickersLastModifiedServer, existing.type);
					const isModified = existing.ts < serverTs.ts;
					for (const tabItem of state.stickerTabItems) {
						if (tabItem.type && tabItem.type == p.type) {
							tabItem.isModified = isModified;
							break;
						}
					}
					break;
				}
			}
		},
		setPanelTitle: (state, payload) => {
			state.localization.panelTitle = payload;
		},
		setPanelServerResponse: (state, payload) => {
			state.panelServerResponse = payload;
		},
		setCurrentPage: (state, payload) => {
			state.currentPage = payload;
		},
	},
	actions: {
		saveAnalytic({ commit }, payload) {
			const { event, params } = payload;
			// eslint-disable-next-line
			gtag('event', event, {
				...params,
			});
		},
	},
	modules: {},
});

const findItemByType = (arr, type) => {
	return arr.find(a => type && a.type && a.type == type);
};

export default store;
export const useStore = () => store;
